/**
 * What the player's three columns say about the recordings of one movement: the list on the left,
 * the set and the discussion on the right, and the pins on the scrubber. Words and figures only —
 * the element and its clock are the lib hooks'.
 */

import {
  byNewestClip,
  clipAspect,
  clipPersonView,
  commentWhen,
  formatClock,
  isViewer,
  type ClipComment,
  type ClipEntry,
  type ClipPerson,
  type ClipPersonView,
} from '~/entities/exercise-clip';
import {
  anchorWords,
  setCell,
  setHeadline,
  setPosition,
  type LiftSession,
  type SetPosition,
} from '~/entities/lift-history';
import type { Translate } from '~/shared/lib/localeTranslate';

import { daysUntil } from './playback';

interface PlayerDeps {
  readonly t: Translate;
  readonly locale: string;
  readonly now: number;
}

export interface PlayerListRow {
  readonly clipId: number;
  readonly dateShort: string;
  readonly meta: string;
  readonly by: ClipPersonView;
  readonly posterUrl: string | null;
  readonly wide: boolean;
  readonly unread: boolean;
}

interface PlayerSetChip {
  readonly setId: number;
  readonly label: string;
  readonly filmed: boolean;
}

export interface PlayerHeader {
  /** "Seria 3 z 4", or `null` for a clip filmed outside a session. */
  readonly setLine: string | null;
  readonly sessionChip: string | null;
  readonly title: string;
  readonly chips: readonly PlayerSetChip[];
  readonly e1rmLine: string | null;
  readonly outsideNote: string | null;
  readonly by: ClipPersonView;
  readonly expires: string | null;
  readonly kgReps: string | null;
  readonly dateShort: string;
}

export interface PlayerCommentView {
  readonly id: number;
  readonly person: ClipPersonView;
  readonly mine: boolean;
  readonly when: string;
  readonly body: string;
  readonly edited: boolean;
  /** The moment of the film the comment is pinned to, or `null` for a remark about the whole clip. */
  readonly pin: { readonly seconds: number; readonly stamp: string; readonly offsetMs: number } | null;
  readonly pending: boolean;
}

export interface PlayerPin {
  readonly commentId: number;
  readonly seconds: number;
  readonly label: string;
}

interface PlayerViewInput {
  readonly clips: readonly ClipEntry[];
  readonly clipId: number;
  readonly sessions: readonly LiftSession[];
  readonly comments: readonly ClipComment[];
  readonly viewerUserId: string | null;
}

export interface PlayerView {
  readonly rows: readonly PlayerListRow[];
  readonly index: number;
  readonly current: ClipEntry | null;
  readonly header: PlayerHeader | null;
  readonly comments: readonly PlayerCommentView[];
  readonly pins: readonly PlayerPin[];
  /** The clip compared against the current one: the next older, or the newer one for the oldest. */
  readonly compareWith: ClipEntry | null;
}

/** A comment written by this app and not yet confirmed carries a negative id. */
const isPendingComment = (comment: ClipComment): boolean => comment.id < 0;

/** „Seria 3, 80 kg × 5" — the list's line is short, so a counted set names no total. */
function listMeta(position: SetPosition | null, load: string | null, t: Translate): string {
  if (position === null) return t('web:clipPlayer.list.outside');
  switch (position.kind) {
    case 'session':
      return t('web:clipPlayer.list.inSession');
    case 'counted':
    case 'numbered':
      return load === null
        ? t('web:clipPlayer.list.setOnly', { number: position.number })
        : t('web:clipPlayer.list.set', { number: position.number, kgReps: load });
  }
}

export function buildPlayerView(input: PlayerViewInput, { t, locale, now }: PlayerDeps): PlayerView {
  const one = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const shortDay = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
  const shortDayYear = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  /** "22 wrz", and "3 paź 2025" for a day outside the current year. */
  const dayMonth = {
    format: (date: Date) =>
      (date.getFullYear() === new Date(now).getFullYear() ? shortDay : shortDayYear).format(date),
  };
  const fullDate = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' });
  const clips = [...input.clips].sort(byNewestClip);
  const index = clips.findIndex((clip) => clip.id === input.clipId);
  const current = clips[index] ?? null;

  const words = { t, locale };
  const person = (who: ClipPerson): ClipPersonView =>
    clipPersonView(who, input.viewerUserId, t('web:forma.you'));
  const positionOf = (clip: ClipEntry) =>
    clip.anchor === null ? null : setPosition(input.sessions, clip.anchor);

  const rows: readonly PlayerListRow[] = clips.map((clip) => {
    const load = clip.anchor === null ? null : setHeadline(clip.anchor, words);
    const position = positionOf(clip);
    return {
      clipId: clip.id,
      dateShort: dayMonth.format(new Date(clip.performedOn)),
      meta: listMeta(position, load, t),
      by: person(clip.author),
      posterUrl: clip.media.posterUrl,
      wide: clipAspect(clip.media) > 1,
      unread: clip.unread,
    };
  });

  const header = ((): PlayerHeader | null => {
    if (current === null) return null;
    const anchor = current.anchor;
    const session =
      anchor === null
        ? null
        : (input.sessions.find((entry) => entry.workoutExecutionId === anchor.workoutExecutionId) ??
          null);
    const expires =
      current.expiresSoon && current.expiresAt !== null
        ? t('web:clipPlayer.set.expires', { count: daysUntil(current.expiresAt, now) })
        : null;
    const dateShort = dayMonth.format(new Date(current.performedOn));
    if (anchor === null) {
      return {
        setLine: null,
        sessionChip: null,
        title: fullDate.format(new Date(current.performedOn)),
        chips: [],
        e1rmLine: null,
        outsideNote: t('web:clipPlayer.set.outsideNote'),
        by: person(current.author),
        expires,
        kgReps: null,
        dateShort,
      };
    }
    const set = anchorWords(anchor, input.sessions, words);
    const kgReps = set.load;
    return {
      setLine: set.place,
      sessionChip: t('web:clipPlayer.set.session', { date: dateShort }),
      title: kgReps ?? fullDate.format(new Date(current.performedOn)),
      chips: (session?.sets ?? []).map((entry) => ({
        setId: entry.setId,
        label: setCell(entry, words) ?? '·',
        filmed: entry.setId === anchor.setId,
      })),
      e1rmLine:
        session?.e1rmKg == null
          ? null
          : t('web:clipPlayer.set.e1rm', { value: one.format(session.e1rmKg) }),
      outsideNote: null,
      by: person(current.author),
      expires,
      kgReps,
      dateShort,
    };
  })();

  const comments: readonly PlayerCommentView[] = input.comments.map((comment) => {
    const { offsetMs } = comment;
    return {
      id: comment.id,
      person: person(comment.author),
      mine: isViewer(comment.author, input.viewerUserId),
      when: commentWhen(comment.createdAt, now, locale),
      body: comment.body,
      edited: comment.editedAt !== null,
      pin:
        offsetMs === null
          ? null
          : { seconds: offsetMs / 1000, stamp: formatClock(offsetMs / 1000, true, locale), offsetMs },
      pending: isPendingComment(comment),
    };
  });

  return {
    rows,
    index,
    current,
    header,
    comments,
    pins: commentPins(input.comments, input.viewerUserId, { t, locale }),
    compareWith: index === -1 ? null : (clips[index + 1] ?? clips[index - 1] ?? null),
  };
}

/**
 * The scrubber's pins: one per comment pinned to a moment, named after its author and the moment.
 * Both players draw them from here, the full one over the whole thread and the side panel's over the
 * newest page the movement's timeline carries.
 */
export function commentPins(
  comments: readonly ClipComment[],
  viewerUserId: string | null,
  { t, locale }: Pick<PlayerDeps, 't' | 'locale'>,
): readonly PlayerPin[] {
  return comments.flatMap(({ id, author, offsetMs }) =>
    offsetMs === null
      ? []
      : [
          {
            commentId: id,
            seconds: offsetMs / 1000,
            label: t('web:clipPlayer.controls.pin', {
              who: clipPersonView(author, viewerUserId, t('web:forma.you')).name,
              time: formatClock(offsetMs / 1000, true, locale),
            }),
          },
        ],
  );
}
